import { JSNode } from "csi/json/JSNode";
import csi from "csi/main";
import xpath from "csi/_base/xpath";

export interface IDefObject {
	isValid?: () => boolean;
}

export type DefElement = Element | JSNode;
export type DefCreateFunction = (element?: DefElement) => IDefObject;

/** look for single child element of given element using given xpath and, if found, pass to create function */
export function getDef(element: DefElement, xpathFilter: string, createFunction: DefCreateFunction): IDefObject | null {
	// <XML ...>
	//     <Tag ... />
	if (!element) {
		return null;
	}

	let e: DefElement;

	if (!xpathFilter) {
		// the special case when the node is filtered already
		e = element;
	} else {
		try {
			e = selectElementByXpath(element, xpathFilter);
		} catch (ex) {
			return null;
		}
	}

	if (e) {
		const result = createFunction(e);

		if (result?.isValid?.() !== false) {
			return result;
		}
	}

	return null;
}

/** look for multiple child elements of given element using given xpath and, if found, pass each to create function */
export function getDefs(element: DefElement | DefElement[], xpathFilter: string, createFunction: DefCreateFunction): IDefObject[] | null {
	// <XML ...>
	//     <Tag ... />
	//     <Tag ... />
	if (!element) {
		return null;
	}

	let elements: DefElement[];
	const elementArray = element as DefElement[];

	if (!xpathFilter && (elementArray.length != null)) {
		// the special case when nodes are filtered already
		elements = elementArray;
	} else {
		try {
			elements = selectElementsByXpath(element as DefElement, xpathFilter);

			if (!elements || (elements.length == 0)) {
				return null;
			}
		} catch (ex) {
			console.warn(ex);
			return null;
		}
	}

	const results: IDefObject[] = [];

	for (const childElement of elements) {
		const result = createFunction(childElement);

		if (result && (result.isValid?.() !== false)) {
			results.push(result);
		}
	}

	return results.length ? results : null;
}

/** extract property of multiple child elements matching given xpath */
export function getProperties(element: DefElement, xpathFilter: string, prop: string): string[] | null {
	// <XML ...>
	//     <Tag P="..." />
	//     <Tag P="..." />
	const elements = selectElementsByXpath(element, xpathFilter);
	return csi.transform(elements, (e) => e.getAttribute(prop));
}

/** extract property of given element, returning default value if not found */
export function getProperty(element: DefElement, prop: string, defaultValue?: any): any {
	// <XML P="...">
	try {
		return element?.getAttribute(prop) ?? defaultValue;
	} catch (ex) {
	}

	return defaultValue;
}

/** extract boolean value from property of given element, returning default value if not found */
export function getBooleanProperty(element: DefElement, prop: string, defaultValue?: boolean): boolean | null {
	// <XML P="...">
	const value = getProperty(element, prop, defaultValue);
	return (value == null) ? value : (value === true || value === "true" || value === 1 || value === "1");
}

/** select single child element of given element using given xpath */
export function selectElementByXpath(element: DefElement, xpathFilter: string): DefElement | null {
	if (!element) {
		return null;
	}

	const jsNode = element as JSNode;
	if (jsNode.selectSingleNode) {
		return jsNode.selectSingleNode(xpathFilter);
	}

	const elements = xpath.selectNodes(element, xpathFilter);
	return elements?.length ? elements[0] : null;
}

/** select multiple child element of given element using given xpath */
export function selectElementsByXpath(element: DefElement, xpathFilter: string): DefElement[] | null {
	if (!element) {
		return null;
	}

	const jsNode = element as JSNode;
	if (jsNode.selectNodes) {
		return jsNode.selectNodes(xpathFilter);
	}

	const elements = xpath.selectNodes(element, xpathFilter);
	return elements?.length ? elements : null;
}
